import { useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import { isPrivatePath } from "@/lib/privatePaths";

const LANGS = ["fr", "de", "it", "en"];
const OG_LOCALE = { fr: "fr_CH", de: "de_CH", it: "it_CH", en: "en_US" };

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!content) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.querySelector(sel);
  if (!href) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export const Seo = ({ title, description, image, jsonLd, canonicalPath, ogType = "website", noindex = false }) => {
  const { lang } = useLang();
  useEffect(() => {
    const origin = window.location.origin;
    const path = canonicalPath || window.location.pathname;
    const canonical = origin + path;
    const fullTitle = title ? `${title} · swiss-pagan.ch` : "swiss-pagan.ch — Medieval, Viking & Pagan Culture in Switzerland";
    document.title = fullTitle;
    document.documentElement.lang = lang;

    setMeta("description", description);
    setMeta("robots", (noindex || isPrivatePath(path)) ? "noindex, nofollow" : null);
    setMeta("og:site_name", "swiss-pagan.ch", "property");
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:image", image, "property");
    setMeta("og:locale", OG_LOCALE[lang] || "fr_CH", "property");

    setMeta("twitter:card", image ? "summary_large_image" : "summary");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    setLink("canonical", canonical);
    // hreflang alternates (URL carries ?lang=xx which LanguageContext honours)
    LANGS.forEach((l) => setLink("alternate", `${origin}${path}?lang=${l}`, l));
    setLink("alternate", canonical, "x-default");

    let script = document.getElementById("jsonld-seo");
    if (jsonLd) {
      if (!script) { script = document.createElement("script"); script.type = "application/ld+json"; script.id = "jsonld-seo"; document.head.appendChild(script); }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
    return () => { const s = document.getElementById("jsonld-seo"); if (s) s.remove(); };
  }, [title, description, image, jsonLd, canonicalPath, ogType, lang, noindex]);

  return null;
};

export default Seo;
